import { Button, Dialog, Typography } from "@material-ui/core"
import CircularProgress from "@material-ui/core/CircularProgress"
import DialogContent from "@material-ui/core/DialogContent"
import Fab from "@material-ui/core/Fab"
import * as React from "react"
import { useEffect, useState } from "react"
import { Redirect } from "react-router"
import { AppRoutes } from "../../common/AppsRouter"
import { CCUtils } from "../../common/CCUtils"
import { OverrideKeys } from "../../shared/models/ActivityProgress"
import { Student } from "../../shared/models/Student"
import { ActivityProgressStoreSA } from "../activity-progress/ActivityProgressStoreSA"
import { appTheme, spacing } from "../StyleUtils"
import { AuthStoreSA } from "./AuthStoreSA"

interface LoginDialogProps {
    dialogIsOpen: boolean
    onClose: () => void
    student: Student
    existingAccount?: boolean
}

export const LoginDialog: React.FC<LoginDialogProps> = ({
    dialogIsOpen,
    onClose,
    student,
    existingAccount,
}) => {
    const [restrictions, setRestrictions] = useState<{
        title: string
        message: string
    }>()
    const [loading, setLoading] = useState(true)
    const [redirectToClickCity, setRedirect] = useState(false)
    const name = `${student.firstName} (${student.screenname})`

    const login = async () => {
        await AuthStoreSA.instance.loginStudent(
            student.studentId,
            student.birthCode,
        )
        setRedirect(true)
    }

    useEffect(() => {
        if (dialogIsOpen) {
            const checkRestrictions = async () => {
                const restriction =
                    await ActivityProgressStoreSA.instance.hasRestrictions(
                        student,
                    )

                switch (restriction) {
                    case OverrideKeys.PER_DAY: {
                        setRestrictions({
                            title: "Lesson Complete",
                            message: `You've completed your lesson for today. Check with your teacher about when you can continue.`,
                        })
                        break
                    }
                    case OverrideKeys.PER_WEEK: {
                        setRestrictions({
                            title: "Lessons Complete",
                            message: `You've completed your lessons for this week. Check with your teacher about when you can continue.`,
                        })
                        break
                    }
                    case OverrideKeys.CONSECUTIVE_DAYS: {
                        setRestrictions({
                            title: "Lesson Complete",
                            message: `You completed a lesson yesterday. Check with your teacher about when you can continue.`,
                        })
                        break
                    }
                    case undefined: {
                        setRestrictions({
                            title: "",
                            message: "",
                        })
                        break
                    }
                }

                setLoading(false)
            }
            checkRestrictions()
        }
    }, [dialogIsOpen, student])

    if (redirectToClickCity) {
        return <Redirect to={AppRoutes.activities} />
    }

    let welcome
    let audioFile
    if (existingAccount) {
        welcome = (
            <Typography variant={"h5"}>
                Looks like you already have an account!
            </Typography>
        )
    } else {
        welcome = <Typography variant={"h5"}>Welcome</Typography>
        audioFile = CCUtils.asset("auth/login/if-this-is-your-username.mp3")
    }

    if (loading) {
        return (
            <Dialog open={dialogIsOpen}>
                <CircularProgress />
            </Dialog>
        )
    }

    return (
        <Dialog open={dialogIsOpen} onClose={() => onClose()}>
            <audio src={audioFile} autoPlay />
            <DialogContent
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: appTheme.spacing(4, 8),
                }}
            >
                {restrictions.title !== "" ? (
                    <>
                        <Typography variant={"h5"}>
                            {restrictions.title}
                        </Typography>
                        <Typography variant={"h6"}>{name}</Typography>
                        <Typography
                            variant={"body1"}
                            style={{
                                textAlign: "center",
                                marginTop: appTheme.spacing(2),
                            }}
                        >
                            {restrictions.message}
                        </Typography>
                        <Fab
                            variant={"extended"}
                            color={"primary"}
                            onClick={onClose}
                            style={{ marginTop: spacing(2) }}
                        >
                            Close
                        </Fab>
                    </>
                ) : (
                    <>
                        {welcome}
                        <Typography variant={"h6"}>{name}</Typography>
                        <Fab
                            variant={"extended"}
                            color={"primary"}
                            onClick={login}
                            style={{ marginTop: spacing(2) }}
                        >
                            Next
                        </Fab>
                        <Button
                            color={"primary"}
                            onClick={() => onClose()}
                            style={{ marginTop: spacing(2) }}
                        >
                            Back
                        </Button>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
